import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense, useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthRoutes } from "./auth/AuthRoutes";
import { ClientAccessRoutes, FullAccessRoutes } from "./auth/FullAccessRoutes";
import { PageAcceptInvite } from "./auth/PageAcceptInvite";
import { PageForgotPassword } from "./auth/PageForgotPassword";
import { PageForgotPasswordConsume } from "./auth/PageForgotPasswordConsume";
import { PageSignIn } from "./auth/PageSignIn";
import { PageSignUp } from "./auth/PageSignUp";
import { PageVerifyResend } from "./auth/PageVerifyResend";
import { PagePaymentSimulation } from "./billing/PagePaymentSimulation";
import { PageBlogPost } from "./blog/PageBlogPost";
import { PageBlogPostCreate } from "./blog/PageBlogPostCreate";
import { PageCalendar } from "./calendar/PageCalendar";
import { AlertRootScope } from "./common/alert/AlertRootScope";
import { Dialog } from "./common/dialog/Dialog";
import { ModalContext } from "./common/modal/ModalContext";
import { GenericErrorBoundary } from "./components/GenericError";
import { PageMediaEdit } from "./content/PageMediaEdit";
import { PageMediaLibrary } from "./content/PageMediaLibrary";
import { PageOnboard } from "./dashboard/onboard/PageOnboard";
import { PageDashboard } from "./dashboard/PageDashboard";
import { ExternalScripts } from "./ExternalScripts";
import { PageNotFound } from "./error/PageNotFound";
import { PageInbox } from "./inbox/PageInbox";
import { LinkGen } from "./LinkGen";
import { AppLayout } from "./layout/AppLayout";
import { AuthLayout } from "./layout/AuthLayout";
import { PageLoading } from "./loading/PageLoading";
import { MetaAdsLayout } from "./meta-ads/MetaAdsLayout";
import { PageApprovalReview } from "./meta-ads/PageApprovalReview";
import { PageCampaignCreate } from "./meta-ads/PageCampaignCreate";
import { PageCampaignTemplates } from "./meta-ads/PageCampaignTemplates";
import { PageMetaAds } from "./meta-ads/PageMetaAds";
import { PageMetaAdsAdminConnections } from "./meta-ads/PageMetaAdsAdminConnections";
import { PageMetaAdsApprovals } from "./meta-ads/PageMetaAdsApprovals";
import { PageMetaAdsAudiences } from "./meta-ads/PageMetaAdsAudiences";
import { PageMetaAdsLeads } from "./meta-ads/PageMetaAdsLeads";
import { PageMetaAdsOAuthResult } from "./meta-ads/PageMetaAdsOAuthResult";
import { PageMetaAdsReporting } from "./meta-ads/PageMetaAdsReporting";
import { PageMetaAdsReports } from "./meta-ads/PageMetaAdsReports";
import { PageMetaAdsTeam } from "./meta-ads/PageMetaAdsTeam";
import { PageGoogleMyBusiness } from "./my-business/PageGoogleMyBusiness";
import { PagePost } from "./post/PagePost";
import { PagePostCreate } from "./post/PagePostCreate";
import { PageShortsCreate } from "./post/PageShortsCreate";
import { PageShortsQueue } from "./post/PageShortsQueue";
import { PageReport } from "./report/PageReport";
import PageSeo from "./seo/PageSeo";
import { SeoAuditPage } from "./seo-audit/SeoAuditPage";
import { PageCanvaAudit } from "./settings/PageCanvaAudit";
import { PageSettings } from "./settings/PageSettings";
import { PageXSpendAdmin } from "./settings/PageXSpendAdmin";
import { PageTikTokAds } from "./tiktok-ads/PageTikTokAds";
import { PageTikTokCampaignCreate } from "./tiktok-ads/PageTikTokCampaignCreate";
import { PageVideoAdmin } from "./video/PageVideoAdmin";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1_000, // 5 mins
			retry: false,
		},
	},
});

function App() {
	// handle TEST tag in the corner of the page
	useEffect(() => {
		if (import.meta.env.PUBLIC_ENV !== "production") {
			document.body.classList.add("test-environment");
		}
	}, []);

	return (
		<>
			<ExternalScripts />
			<AlertRootScope>
				<Suspense fallback={<PageLoading />}>
					<QueryClientProvider client={queryClient}>
						<GenericErrorBoundary>
							<CookiesProvider>
								<BrowserRouter>
									<Dialog.Provider>
										<ModalContext>
											<Routes>
												{/*Authenticated and non-authenticated*/}
												<Route element={<AuthLayout />}>
													<Route
														path={LinkGen.AcceptInvite(":token")}
														element={<PageAcceptInvite />}
													/>
												</Route>
												<Route element={<AuthRoutes auth={false} />}>
													<Route element={<AuthLayout />}>
														<Route
															path={LinkGen.Login()}
															element={<PageSignIn />}
														/>
														<Route
															path={LinkGen.Register()}
															element={<PageSignUp />}
														/>
														<Route
															path={LinkGen.PasswordResetRequest()}
															element={<PageForgotPassword />}
														/>
														<Route
															path={LinkGen.PasswordResetConsume(
																":id",
																":token",
															)}
															element={<PageForgotPasswordConsume />}
														/>
														<Route
															path={LinkGen.VerificationResend()}
															element={<PageVerifyResend />}
														/>
													</Route>
												</Route>
												<Route element={<AuthRoutes auth={true} />}>
													<Route element={<AppLayout />}>
														{/* Routes accessible by all users including calendar-only */}
														<Route
															path={LinkGen.Calendar(":date", ":type", ":view")}
															element={<PageCalendar />}
														/>
														<Route
															path={LinkGen.Post(":id")}
															element={<PagePost />}
														/>
														<Route
															path={LinkGen.PostCreate()}
															element={<PagePostCreate />}
														/>
														<Route
															path={LinkGen.ShortsCreate()}
															element={<PageShortsCreate />}
														/>
														<Route
															path={LinkGen.ShortsQueue()}
															element={<PageShortsQueue />}
														/>

														{/* Routes accessible by full-access and client account users */}
														<Route element={<ClientAccessRoutes />}>
															<Route
																path={LinkGen.Report()}
																element={<PageReport />}
															/>
														</Route>

														{/* Routes restricted to full-access users only */}
														<Route element={<FullAccessRoutes />}>
															<Route
																path={LinkGen.Dashboard()}
																element={<PageDashboard />}
															/>
															<Route
																path={LinkGen.Onboard()}
																element={<PageOnboard />}
															/>
															<Route
																path={LinkGen.Inbox()}
																element={<PageInbox />}
															/>
															<Route
																path={LinkGen.Settings.Tab(":tab")}
																element={<PageSettings />}
															/>
															<Route
																path={LinkGen.Settings.CanvaAudit()}
																element={<PageCanvaAudit />}
															/>
															<Route
																path={LinkGen.Settings.XSpendAdmin()}
																element={<PageXSpendAdmin />}
															/>
															<Route
																path={LinkGen.Settings.VideoAdmin()}
																element={<PageVideoAdmin />}
															/>
															<Route
																path={LinkGen.Media()}
																element={<PageMediaLibrary />}
															/>
															<Route
																path={LinkGen.MediaEdit(":id")}
																element={<PageMediaEdit />}
															/>
															<Route
																path={LinkGen.BlogPost(":id")}
																element={<PageBlogPost />}
															/>
															<Route
																path={LinkGen.BlogPostCreate()}
																element={<PageBlogPostCreate />}
															/>
															<Route
																path={LinkGen.PaymentSimulation()}
																element={<PagePaymentSimulation />}
															/>
															<Route
																path={LinkGen.Seo()}
																element={<PageSeo />}
															/>
															<Route
																path="/seo-keywords"
																element={
																	<Navigate to={LinkGen.Seo()} replace />
																}
															/>
															<Route
																path={LinkGen.SeoAudit()}
																element={<SeoAuditPage />}
															/>
															<Route
																path="/ai-visibility"
																element={
																	<Navigate to={LinkGen.Seo()} replace />
																}
															/>
															<Route element={<MetaAdsLayout />}>
																<Route
																	path={LinkGen.MetaAds.CampaignCreate()}
																	element={<PageCampaignCreate />}
																/>
																<Route
																	path="/meta-ads/campaigns/edit/:campaignId"
																	element={<PageCampaignCreate />}
																/>
																<Route
																	path="/meta-ads/campaigns/drafts/:draftId/edit"
																	element={<PageCampaignCreate />}
																/>
																<Route
																	path={LinkGen.MetaAds.Index()}
																	element={<PageMetaAds />}
																/>
																<Route
																	path={LinkGen.MetaAds.Leads()}
																	element={<PageMetaAdsLeads />}
																/>
																<Route
																	path={LinkGen.MetaAds.Audiences()}
																	element={<PageMetaAdsAudiences />}
																/>
																<Route
																	path={LinkGen.MetaAds.Reporting()}
																	element={<PageMetaAdsReporting />}
																/>
																<Route
																	path={LinkGen.MetaAds.Reports()}
																	element={<PageMetaAdsReports />}
																/>
																<Route
																	path={LinkGen.MetaAds.CampaignTemplates()}
																	element={<PageCampaignTemplates />}
																/>
																<Route
																	path={LinkGen.MetaAds.Approvals()}
																	element={<PageMetaAdsApprovals />}
																/>
																<Route
																	path={LinkGen.MetaAds.Team()}
																	element={<PageMetaAdsTeam />}
																/>
																<Route
																	path={LinkGen.MetaAds.AdminConnections()}
																	element={<PageMetaAdsAdminConnections />}
																/>
																<Route
																	path={LinkGen.MetaAds.ApprovalReview(":id")}
																	element={<PageApprovalReview />}
																/>
																<Route
																	path={LinkGen.MetaAds.OAuthResult()}
																	element={<PageMetaAdsOAuthResult />}
																/>
															</Route>
															<Route
																path={LinkGen.TikTokAds.Index()}
																element={<PageTikTokAds />}
															/>
															<Route
																path={LinkGen.TikTokAds.CampaignCreate()}
																element={<PageTikTokCampaignCreate />}
															/>
															<Route
																path={LinkGen.TikTokAds.DraftEdit(":draftId")}
																element={<PageTikTokCampaignCreate />}
															/>
															<Route
																path={LinkGen.Google.MyBusiness.Tab(":tab")}
																element={<PageGoogleMyBusiness />}
															/>
														</Route>
													</Route>
												</Route>
												<Route path="*" element={<PageNotFound />} />
											</Routes>

											<ReactQueryDevtools
												buttonPosition="top-left"
												initialIsOpen={false}
											/>
										</ModalContext>
									</Dialog.Provider>
								</BrowserRouter>
							</CookiesProvider>
						</GenericErrorBoundary>
					</QueryClientProvider>
				</Suspense>
			</AlertRootScope>
		</>
	);
}

export default App;
